import React, { useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Card from "@material-ui/core/Card";
import moment from "moment";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import IconButton from "@material-ui/core/IconButton";
import Collapse from "@material-ui/core/Collapse";
import AuthContext from "../../../../context/auth-context";

const STATUS_STYLE = {
  executed: { label: "Concluído", color: "#297835", background: "#e4f3e6" },
}

const DEFAULT_STATUS_STYLE = { color: "#6f6f6f", background: "#f1f2f7" }

function BacktestStatusIndicator(props) {
  const { status, processedCount, totalCount } = props
  const { t } = useTranslation()

  if (status === "created" || status === "pending_sent_to_queue" || status === "waiting_analysis") {
    const percentage = totalCount ? Math.round((processedCount / totalCount) * 100) : 0
    return (
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "10px", width: "100%", minWidth: "110px" }}>
        <div style={{ flex: 1, height: "10px", backgroundColor: "#e4f3e6", borderRadius: "5px", overflow: "hidden" }}>
          <div style={{ width: `${percentage}%`, height: "100%", backgroundColor: "#297835", borderRadius: "5px" }} />
        </div>
        <span className="normalText normalMediumSize bold" style={{ color: "#297835", whiteSpace: "nowrap" }}>
          {percentage}%
        </span>
      </div>
    )
  }

  const style = STATUS_STYLE[status] || DEFAULT_STATUS_STYLE
  return (
    <p
      className="coloredStatus normalMediumSize normalText"
      style={{ backgroundColor: style.background, color: style.color, minWidth: "71px" }}
    >
      {style.label ? t(style.label) : t(status)}
    </p>
  )
}

function BacktestCard(props) {
  const { backtest, gridTemplateColumns, showCompanyColumn } = props

  const user_language = useContext(AuthContext).user_data.locale;
  const canUseMultiEnvironment = useContext(AuthContext).user_data.roles.includes("use_sa_rule_engine");
  const { t } = useTranslation();

  const [expanded, setExpanded] = useState(false);

  const handleExpand = (event) => {
    event.stopPropagation();
    setExpanded(!expanded);
  };

  const formatDate = (date) => {
    if (!date) return " - "
    return user_language === "en"
      ? moment(date).format("MM/DD/YYYY HH:mm:ss")
      : moment(date).format("DD/MM/YYYY HH:mm:ss")
  }

  const formatFilterDate = (date) => {
    if (!date) return " - "
    return user_language === "en"
      ? moment.utc(date).format("MM/DD/YYYY")
      : moment.utc(date).format("DD/MM/YYYY")
  }

  const progressPercentage = backtest.total_count
    ? Math.round((backtest.processed_count / backtest.total_count) * 100)
    : 0

  return (
    <div style={{ marginBottom: "5px" }}>
      <Card className="listCard">
        <div
          style={{ display: "flex", flexDirection: "column", width: "100%" }}
        >
          <div
            style={{ display: "grid", gridTemplateColumns: gridTemplateColumns, cursor: "pointer" }}
            onClick={handleExpand}
          >
            {showCompanyColumn && (
              <div
                className={["listCardItem", "normalText", "normalMediumSize", "bold"].join(" ")}
                style={{ textAlign: "center" }}
              >
                {backtest.company_name || "-"}
              </div>
            )}
            <div
              className={["listCardItem", "normalText", "normalMediumSize", "bold", "breakWord"].join(" ")}
              style={{ textAlign: "center" }}
            >
              {backtest.name || "-"}
            </div>
            <div
              className={["listCardItem", "normalText", "normalMediumSize"].join(" ")}
              style={{ textAlign: "center" }}
            >
              <BacktestStatusIndicator
                status={backtest.status}
                processedCount={backtest.processed_count}
                totalCount={backtest.total_count}
              />
            </div>
            <div
              className={["listCardItem", "normalText", "normalMediumSize", "noWrap"].join(" ")}
              style={{ textAlign: "center" }}
            >
              {formatDate(backtest.created_at)}
            </div>
            <div
              className={["listCardItem", "normalText", "subtitleSize", "noWrap"].join(" ")}
              style={{ textAlign: "center" }}
            >
              <IconButton onClick={handleExpand}>
                {expanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              </IconButton>
            </div>
          </div>
          <Collapse in={expanded} timeout="auto" unmountOnExit style={{ margin: "10px" }}>
            <div style={{ paddingTop: "20px", paddingBottom: "20px" }}>
              <div
                className="internalCollapse twoColumn"
                style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}
              >
                <div>
                  <div className={["blueText", "normalMediumSize"].join(" ")}>{t("Evento")}</div>
                  <div className="normalText normalMediumSize">{t(backtest.event_type)}</div>
                </div>
                {canUseMultiEnvironment && (
                  <div>
                    <div className={["blueText", "normalMediumSize"].join(" ")}>{t("Região")}</div>
                    <div className="normalText normalMediumSize">{backtest.region === "sa_east_1" ? t("América do Sul") : t("América do Norte")}</div>
                  </div>
                )}
                <div>
                  <div className={["blueText", "normalMediumSize"].join(" ")}>{t("Período")}</div>
                  <div className="normalText normalMediumSize">
                    {formatFilterDate(backtest.filters?.initial_date)} - {formatFilterDate(backtest.filters?.end_date)}
                  </div>
                </div>
                <div>
                  <div className={["blueText", "normalMediumSize"].join(" ")}>{t("Regra atual no momento da análise")}</div>
                  <div className="normalText normalMediumSize">
                    {backtest.rule_key_current ? (
                      <Link
                        className="link"
                        to={"/rule/" + backtest.rule_key_current + "/" + backtest.rule_version_current}
                      >
                        {t("Versão")} {backtest.rule_version_current}
                      </Link>
                    ) : "-"}
                  </div>
                </div>
                <div>
                  <div className={["blueText", "normalMediumSize"].join(" ")}>{t("Regra candidata")}</div>
                  <div className="normalText normalMediumSize">
                    {backtest.rule_key_candidate ? (
                      <Link
                        className="link"
                        to={backtest.rule_version_candidate
                          ? "/rule/" + backtest.rule_key_candidate + "/" + backtest.rule_version_candidate
                          : "/rule/" + backtest.rule_key_candidate + "/rule_draft"}
                      >
                        {backtest.rule_version_candidate ? `${t("Versão")} ${backtest.rule_version_candidate}` : t("Rascunho")}
                      </Link>
                    ) : "-"}
                  </div>
                </div>
                <div>
                  <div className={["blueText", "normalMediumSize"].join(" ")}>{t("Analista")}</div>
                  <div className="normalText normalMediumSize">
                    {backtest.analyst_data?.analyst_name
                      ? `${backtest.analyst_data.analyst_name} (${backtest.analyst_data.analyst_email})`
                      : "-"}
                  </div>
                </div>
                <div>
                  <div className={["blueText", "normalMediumSize"].join(" ")}>{t("Progresso")}</div>
                  <div className="normalText normalMediumSize">
                    {backtest.processed_count} / {backtest.total_count} ({progressPercentage}%)
                  </div>
                </div>
              </div>
              {(backtest.status === "executed" || backtest.status === "waiting_analysis" || backtest.status === "pending_sent_to_queue") && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
                  <Link
                    style={{ padding: "5px 30px", textDecoration: "none" }}
                    className={["button", "link", "standard", "normalText", "normalMediumSize"].join(" ")}
                    to={"/backtest/" + backtest.company_key + "/" + backtest.backtest_key + "/report"}
                  >
                    {backtest.status === "executed" ? t("RELATÓRIO") : t("RELATÓRIO PARCIAL")}
                  </Link>
                </div>
              )}
            </div>
          </Collapse>
        </div>
      </Card>
    </div>
  )
}

export default BacktestCard
